import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiSettings } from '../config/api-settings';
import { User } from '../models';

export interface NotificationSettings {
  lowBalanceAlerts: boolean;
  lowBalanceThresholdCents: number | null;
  paymentReceipts: boolean;
  usageAlerts: boolean;
  usageAlertThresholdCents: number | null;
  currency: string;
}

/** Only the fields that changed go in a PATCH the backend keys this by the
 *  same snake_case names GET returns, so callers build this straight off it. */
export type NotificationSettingsPatch = Partial<{
  low_balance_alerts: boolean;
  low_balance_threshold_cents: number | null;
  payment_receipts: boolean;
  usage_alerts: boolean;
  usage_alert_threshold_cents: number | null;
}>;

export interface TwoFactorSetup {
  secret: string;
  otpauthUri: string;
}

export interface TwoFactorStatus {
  enabled: boolean;
  enabledAt: string | null;
  backupCodesRemaining: number;
}

function toNotificationSettings(res: any): NotificationSettings {
  const n = res.notifications;
  return {
    lowBalanceAlerts: !!n.low_balance_alerts,
    lowBalanceThresholdCents: n.low_balance_threshold_cents ?? null,
    paymentReceipts: !!n.payment_receipts,
    usageAlerts: !!n.usage_alerts,
    usageAlertThresholdCents: n.usage_alert_threshold_cents ?? null,
    currency: n.currency,
  };
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  constructor(private http: HttpClient) {}

  updateProfile(args: { name: string }): Observable<User> {
    const URL = `${ApiSettings.fullPath}me`;

    return this.http.patch<any>(URL, { user: args }).pipe(
      map(res => new User(res.user)),
    );
  }

  changePassword(args: {
    currentPassword: string;
    password: string;
    passwordConfirmation: string;
  }): Observable<{ message: string }> {
    const URL = `${ApiSettings.fullPath}me/password`;

    const body = {
      user: {
        current_password: args.currentPassword,
        password: args.password,
        password_confirmation: args.passwordConfirmation,
      },
    };

    return this.http.patch<any>(URL, body).pipe(
      map(res => ({ message: res.message })),
    );
  }

  uploadAvatar(file: File): Observable<void> {
    const URL = `${ApiSettings.fullPath}settings/avatar`;
    const formData = new FormData();
    formData.append('avatar', file);

    return this.http.post<void>(URL, formData);
  }

  deleteAccount(): Observable<void> {
    const URL = `${ApiSettings.fullPath}settings/account`;

    return this.http.delete<void>(URL);
  }

  getNotifications(): Observable<NotificationSettings> {
    const URL = `${ApiSettings.fullPath}me/notifications`;

    return this.http.get<any>(URL).pipe(map(toNotificationSettings));
  }

  updateNotifications(patch: NotificationSettingsPatch): Observable<NotificationSettings> {
    const URL = `${ApiSettings.fullPath}me/notifications`;

    return this.http.patch<any>(URL, { notifications: patch }).pipe(map(toNotificationSettings));
  }

  /** `secret`/`otpauth_uri` live at the root here a `two_factor` key is also present,
   *  but it only echoes the (still-disabled) status, not the setup payload. */
  setupTwoFactor(): Observable<TwoFactorSetup> {
    const URL = `${ApiSettings.fullPath}me/two_factor`;

    return this.http.post<any>(URL, {}).pipe(
      map(res => ({ secret: res.secret, otpauthUri: res.otpauth_uri })),
    );
  }

  /** Turns 2FA on and hands back the 10 recovery codes this is the only
   *  time the backend ever sends them, so callers must show them now.
   *  Same convention as setupTwoFactor(): the payload (backup_codes) is at
   *  the root, alongside a `two_factor` key that just echoes the status. */
  confirmTwoFactor(code: string): Observable<{ backupCodes: string[] }> {
    const URL = `${ApiSettings.fullPath}me/two_factor/confirm`;

    return this.http.post<any>(URL, { code }).pipe(
      map(res => ({ backupCodes: res.backup_codes })),
    );
  }

  /** The only one of the four where the backend's `two_factor` key *is* the payload,
   *  not just a status echo alongside it (there's nothing else in the response). */
  getTwoFactorStatus(): Observable<TwoFactorStatus> {
    const URL = `${ApiSettings.fullPath}me/two_factor`;

    return this.http.get<any>(URL).pipe(
      map(res => {
        const body = res.two_factor ?? res;
        return {
          enabled: !!body.enabled,
          enabledAt: body.enabled_at ?? null,
          backupCodesRemaining: body.backup_codes_remaining,
        };
      }),
    );
  }

  /** Accepts either a 6-digit app code or one of the recovery codes. */
  disableTwoFactor(code: string): Observable<void> {
    const URL = `${ApiSettings.fullPath}me/two_factor`;

    return this.http.delete<void>(URL, { body: { code } });
  }
}
